/* eslint-disable import/no-cycle */
import { ChatStatus } from '@src/model/frontendmodel';
import {
  endedCall,
  missedCall,
  receivedCall,
  rejectedCall,
  startedCall,
} from '@src/reducers/calls';
import callsApi from '@src/reducers/callsAPI';
import {
  activeChatSuccess,
  decrementTotalCallBot,
  decrementTotalCallsOnHold,
  decrementTotalCallsRequest,
  incrementTotalCallBot,
  incrementTotalCallsOnHold,
  incrementTotalCallsRequest,
} from '@src/reducers/chats';
import { selectCallById, selectIsCallEnded } from '@src/selectors/calls';
import { chatByIdSelector } from '@src/selectors/chats';
import { getConsolePreferenceShowBotsInbox } from '@src/selectors/preferences';
import { selectAuthenticationInfo } from '@src/selectors/user';
import store from '@src/store/reduxstore'; //TODO: fix circular dependency from agentchatmanager
import { CallStatus } from '@src/types/calls';
import { ChatSideType } from '@src/types/chats';
import AgentChatManager from '../agentchatmanager';
import { CallMediaSingleton } from '../call';
import { callEventsBus } from './callEventsBus';

const setupCallEventsListener = () => {
  callEventsBus.on([CallStatus.WAITING_AGENT], call => {
    store.dispatch(receivedCall(call));
  });

  callEventsBus.on(CallStatus.WAITING_ANSWER, async call => {
    if (!call?.session?.sdp) return;
    // The agent cancelled this outgoing call while it rang and Meta's answer was
    // already on its way: completing it would build a leg for a call that is off
    // screen, with no button left to hang it up.
    if (selectIsCallEnded(call.id)(store.getState())) return;

    store.dispatch(activeChatSuccess(call.chatId));

    const state = store.getState();
    const chat = chatByIdSelector(call.chatId)(state);
    const agentId = selectAuthenticationInfo(state).id;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mutationState = (state as any).callsApi?.mutations?.calls;
    const sdpOfferHandleId = mutationState?.data?.sdpOfferHandleId;
    // F004: roomId is stored in response data via transformResponse because
    // RTK Query drops originalArgs when fixedCacheKey is used across instances.
    const roomId = mutationState?.data?.roomId as number | undefined;

    if (!mutationState?.data || !sdpOfferHandleId) return;

    if (!chat.assignedTo?.id)
      AgentChatManager.requestChatPickup(call.chatId, agentId);

    await store.dispatch(
      callsApi.endpoints.requestStartOutgoingCall.initiate({
        callId: call.id,
        callContext: {
          roomId,
          call: {
            caller: call.caller,
            callee: call.callee,
            sdpAnswer: call.session.sdp,
            sdpOfferHandleId,
          },
        },
      })
    );

    store.dispatch(receivedCall(call));
  });

  callEventsBus.on(CallStatus.ONGOING, call => {
    const state = store.getState();
    // What the call bar does with it: an ONGOING that finds no call, or an ended one,
    // changes nothing on screen (ENG-2208).
    if (selectIsCallEnded(call.id)(state)) {
      // eslint-disable-next-line no-console
      console.warn('[callEvents] ONGOING ignored: the call already ended', {
        callId: call.id,
      });
    } else if (!selectCallById(call.id)(state)) {
      // eslint-disable-next-line no-console
      console.warn('[callEvents] ONGOING for a call that is not on screen', {
        callId: call.id,
        chatId: call.chatId,
      });
    }
    store.dispatch(startedCall({ callId: call.id, startedAt: call.startedAt }));
  });

  callEventsBus.on([CallStatus.TERMINATED, CallStatus.FAILED], call => {
    CallMediaSingleton.leaveRoom();
    store.dispatch(endedCall(call?.id as string));
  });

  callEventsBus.on(CallStatus.MISSED, call => {
    const existsCall = selectCallById(call.id)(store.getState());

    if (!existsCall) store.dispatch(receivedCall(call));
    else store.dispatch(missedCall(call.id));
  });

  callEventsBus.on(CallStatus.REJECTED, call => {
    // Use leaveRoom() instead of cleanup() so the Janus handle and
    // RTCPeerConnection are closed before stopping local tracks — required
    // by Safari to release the mic indicator.
    CallMediaSingleton.leaveRoom();
    const existsCall = selectCallById(call.id)(store.getState());
    if (existsCall) store.dispatch(rejectedCall(call.id));
  });

  // NOTE: Contador de llamadas - Track which side each call was counted in
  const callCountContext: Record<string, ChatSideType> = {};

  callEventsBus.on(
    [CallStatus.WAITING_ANSWER, CallStatus.WAITING_AGENT],
    (call, emitOptions) => {
      if (emitOptions?.sync) return;
      if (callCountContext[call.id]) return;

      const chat = chatByIdSelector(call.chatId)(store.getState());
      const showBotsInbox = getConsolePreferenceShowBotsInbox(store.getState());

      let chatSide: ChatSideType;
      if (chat.assignedTo?.id) {
        chatSide = ChatSideType.ON_HOLD;
        store.dispatch(incrementTotalCallsOnHold());
      } else if (chat.status === ChatStatus.BOT_CHATTING && showBotsInbox) {
        chatSide = ChatSideType.BOT;
        store.dispatch(incrementTotalCallBot());
      } else {
        chatSide = ChatSideType.REQUEST;
        store.dispatch(incrementTotalCallsRequest());
      }

      callCountContext[call.id] = chatSide;
    }
  );

  callEventsBus.on(
    [
      CallStatus.TERMINATED,
      CallStatus.FAILED,
      CallStatus.MISSED,
      CallStatus.ONGOING,
    ],
    (call, emitOptions) => {
      if (emitOptions?.sync) return;

      let chatSide = callCountContext[call.id];

      const chat = chatByIdSelector(call.chatId)(store.getState());
      const showBotsInbox = getConsolePreferenceShowBotsInbox(store.getState());

      if (chat.assignedTo?.id) {
        chatSide = ChatSideType.ON_HOLD;
      } else if (chat.status === ChatStatus.BOT_CHATTING && showBotsInbox) {
        chatSide = ChatSideType.BOT;
      } else if (chat.status === ChatStatus.OPENED) {
        chatSide = ChatSideType.REQUEST;
      }

      switch (chatSide) {
        case ChatSideType.ON_HOLD:
          store.dispatch(decrementTotalCallsOnHold());
          break;
        case ChatSideType.BOT:
          store.dispatch(decrementTotalCallBot());
          break;
        case ChatSideType.REQUEST:
          store.dispatch(decrementTotalCallsRequest());
          break;
        default:
          break;
      }

      delete callCountContext[call.id];
    }
  );
};

export default setupCallEventsListener;
